/**
 * Admin Push Testing Panel (#28) — testear push delivery, branding y anuncios.
 * Desde acá el super admin puede:
 *  - Enviar un push de prueba a un tenant específico
 *  - Enviar un anuncio broadcast a todos los owners
 *  - Ver el inbox de un tenant (verificar que llegó)
 *  - Ver historial de anuncios
 */
import { useCallback, useEffect, useState } from "react";
import { Send, Radio, Bell, Loader2, CheckCircle2, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { toast } from "sonner";
import { useAppDialogs } from "@/components/ui/app-dialogs";

interface TenantOpt { id: string; name: string; slug: string }
interface Announcement { id: string; title: string; body: string; type: string; sent_at: string | null; created_at: string }
interface NotifRow { id: string; type: string; title: string; body: string; read: boolean; push_delivered: boolean; createdAt: string }

const TYPE_OPTIONS = [
  { value: "info", label: "ℹ️ Información" },
  { value: "warning", label: "⚠️ Advertencia" },
  { value: "release", label: "🚀 Nuevo release" },
  { value: "maintenance", label: "🔧 Mantenimiento" },
];

export function PushTestingPanel() {
  const [tenants, setTenants] = useState<TenantOpt[]>([]);
  const { confirm } = useAppDialogs();
  const [selectedTenant, setSelectedTenant] = useState("");
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [tenantNotifs, setTenantNotifs] = useState<NotifRow[]>([]);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState<"test" | "broadcast" | "history">("test");

  const [testForm, setTestForm] = useState({ title: "🔔 Test de notificación", body: "Esto es una prueba del sistema de push.", url: "/app/pedidos" });
  const [broadcastForm, setBroadcastForm] = useState({ title: "", body: "", url: "", type: "info" });

  const load = useCallback(async () => {
    try {
      const [ts, anns] = await Promise.all([
        apiGetJson<{ id: string; name: string; slug: string }[]>("/api/admin/tenants"),
        apiGetJson<{ announcements: Announcement[] }>("/api/admin/announcements"),
      ]);
      setTenants(ts.map((t) => ({ id: t.id, name: t.name, slug: t.slug })));
      setAnnouncements(anns.announcements ?? []);
    } catch (e) { toast.error(e instanceof Error ? e.message : "Error al cargar"); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const loadTenantInbox = async (tid: string) => {
    if (!tid) return;
    try {
      const d = await apiGetJson<{ notifications: NotifRow[] }>("/api/notifications", tid);
      setTenantNotifs(d.notifications ?? []);
    } catch { setTenantNotifs([]); }
  };

  const sendTest = async () => {
    if (!selectedTenant) { toast.error("Elegí un tenant"); return; }
    setSending(true);
    try {
      await apiSendJson("/api/admin/announcements?action=test", "POST", { ...testForm, tenantId: selectedTenant });
      toast.success("Push de prueba enviado");
      await loadTenantInbox(selectedTenant);
    } catch (e) { toast.error(e instanceof Error ? e.message : "Error"); }
    finally { setSending(false); }
  };

  const sendBroadcast = async () => {
    if (!broadcastForm.title.trim() || !broadcastForm.body.trim()) { toast.error("Título y cuerpo requeridos"); return; }
    if (!(await confirm({ title: "¿Enviar este anuncio a TODOS los owners activos?", description: broadcastForm.title, confirmLabel: "Enviar a todos" }))) return;
    setSending(true);
    try {
      const r = await apiSendJson<{ recipients: number }>("/api/admin/announcements", "POST", broadcastForm);
      toast.success(`Anuncio enviado a ${r.recipients} tenants`);
      setBroadcastForm({ title: "", body: "", url: "", type: "info" });
      await load();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Error"); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 rounded-lg border bg-muted/30 p-1 w-fit">
        {([["test", "🔔 Test push"], ["broadcast", "📢 Broadcast"], ["history", "📋 Historial"]] as [typeof tab, string][]).map(([t, label]) => (
          <button key={t} type="button" onClick={() => setTab(t)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${tab === t ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Test push a tenant específico */}
      {tab === "test" && (
        <div className="space-y-4 max-w-xl">
          <div className="rounded-xl border bg-card p-4 space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-2"><Bell className="h-4 w-4" /> Enviar push de prueba</h3>
            <div className="space-y-2">
              <Label className="text-xs">Tenant destino</Label>
              <select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={selectedTenant}
                onChange={(e) => { setSelectedTenant(e.target.value); void loadTenantInbox(e.target.value); }}>
                <option value="">Elegí un tenant…</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Título</Label><Input value={testForm.title} onChange={(e) => setTestForm((f) => ({ ...f, title: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Cuerpo</Label><Input value={testForm.body} onChange={(e) => setTestForm((f) => ({ ...f, body: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label className="text-xs">URL de destino</Label><Input value={testForm.url} onChange={(e) => setTestForm((f) => ({ ...f, url: e.target.value }))} /></div>
            <Button onClick={sendTest} disabled={sending || !selectedTenant} className="gap-2 w-full">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enviar push de prueba
            </Button>
          </div>

          {/* Inbox del tenant elegido para verificar entrega */}
          {selectedTenant && (
            <div className="rounded-xl border bg-card p-4">
              <h3 className="mb-3 text-sm font-semibold flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /> Inbox del tenant (verifica entrega)</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {tenantNotifs.length === 0 ? <p className="text-xs text-muted-foreground">Sin notificaciones todavía.</p> : (
                  tenantNotifs.slice(0, 10).map((n) => (
                    <div key={n.id} className="rounded-lg border p-2 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{n.title}</span>
                        <div className="flex gap-1">
                          {!n.read && <Badge variant="default" className="text-[10px] h-4">no leída</Badge>}
                          <Badge variant={n.push_delivered ? "default" : "secondary"} className="text-[10px] h-4">{n.push_delivered ? "push ✓" : "push pendiente"}</Badge>
                        </div>
                      </div>
                      <p className="text-muted-foreground mt-0.5">{n.body}</p>
                    </div>
                  ))
                )}
              </div>
              <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => loadTenantInbox(selectedTenant)}>Refrescar inbox</Button>
            </div>
          )}
        </div>
      )}

      {/* Broadcast a todos los owners */}
      {tab === "broadcast" && (
        <div className="space-y-3 max-w-xl">
          <div className="rounded-xl border bg-card p-4 space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-2"><Radio className="h-4 w-4 text-primary" /> Anuncio broadcast (todos los owners activos)</h3>
            <div className="space-y-1.5">
              <Label className="text-xs">Tipo</Label>
              <select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={broadcastForm.type}
                onChange={(e) => setBroadcastForm((f) => ({ ...f, type: e.target.value }))}>
                {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Título *</Label><Input placeholder="Nueva feature disponible" value={broadcastForm.title} onChange={(e) => setBroadcastForm((f) => ({ ...f, title: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Cuerpo *</Label><Textarea rows={3} placeholder="Descripción del anuncio…" value={broadcastForm.body} onChange={(e) => setBroadcastForm((f) => ({ ...f, body: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label className="text-xs">URL (opcional)</Label><Input placeholder="/app/fidelizacion" value={broadcastForm.url} onChange={(e) => setBroadcastForm((f) => ({ ...f, url: e.target.value }))} /></div>
            <Button onClick={sendBroadcast} disabled={sending} variant="default" className="gap-2 w-full">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radio className="h-4 w-4" />} Enviar a todos los owners
            </Button>
          </div>
        </div>
      )}

      {/* Historial de anuncios */}
      {tab === "history" && (
        <div className="space-y-2 max-w-2xl">
          {announcements.length === 0 ? <p className="text-sm text-muted-foreground">Sin anuncios enviados.</p> : (
            announcements.map((a) => (
              <div key={a.id} className="rounded-lg border p-3 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="font-medium">{a.title}</span>
                    <p className="text-xs text-muted-foreground mt-0.5">{a.body}</p>
                  </div>
                  <Badge variant="outline" className="shrink-0 text-[10px]">{a.type}</Badge>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {a.sent_at ? new Date(a.sent_at).toLocaleString("es-AR") : new Date(a.created_at).toLocaleString("es-AR")}
                </p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
